import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';
import { Users, ContactRound, AlertTriangle, ShieldCheck } from 'lucide-react';
import SelectVariants from "./Filters";
import UserAuthDashboard from './AwsServiceInsights';
import { useSecurityData } from './ApiCalls/SecurityDataProvider';
import LoadingCards from './DesignComponents/LoadingCards';
import UnauthorizedPage from "../UnauthorizedPage";
const SecurityPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const { 
    securityData, 
    fetchSecurityData, 
    shouldRefetch, 
    filterState, 
    updateFilters,
    isLoading ,
    lastUpdatedTime,
    authError
  } = useSecurityData();
  
  const { loginData } = securityData;

  const handleFilterChange = (filterType, value) => {
    updateFilters({ [filterType]: value });
  };

  useEffect(() => {
    if (shouldRefetch(filterState)) {
      fetchSecurityData(filterState);
    }
  }, [filterState, shouldRefetch, fetchSecurityData]);

  const failedLogins = loginData?.data?.reduce((count, login) => {
    return login.Status === "Failure" ? count + login.login_count : count;
  }, 0) || 0;

  const successfulLogins = loginData?.data?.reduce((count, login) => {
    return login.Status === "Success" ? count + login.login_count : count;
  }, 0) || 0;

  const successPercentage = loginData ? 
    ((loginData.total_logins - loginData.summary.failed_login_count) / loginData.total_logins * 100).toFixed(2) 
    : 0;

  const renderMetricsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center mb-4">
          <ShieldCheck className="h-8 w-8 text-[#003366]" />
        </div>
        <h3 className="text-xl font-bold text-[#003366] text-center mb-2">Success Percentage</h3>
        <p className="text-center text-sm text-gray-600 mb-4">Logins</p>
        <div className="text-4xl font-bold text-center text-green-600">
          <AnimatedCounter value={successPercentage || 0}/>%
        </div>
      </div>

      <div 
        className="bg-white rounded-lg shadow-lg border border-gray-200 p-6"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center justify-center mb-4">
          <ContactRound className="h-8 w-8 text-[#003366]" />
        </div>
        <h3 className="text-xl font-bold text-[#003366] text-center mb-2">
          Total Login Attempts
        </h3>
        <p className="text-center text-sm text-gray-600 mb-4">
          Last {filterState.duration === "1 day" ? "24 hours" : filterState.duration}
        </p>
        <div className="text-4xl font-bold text-center text-[#003366]">
          {loginData ? loginData.total_logins : 0}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center mb-4">
          <AlertTriangle className="h-8 w-8 text-[#003366]" />
        </div>
        <h3 className="text-xl font-bold text-[#003366] text-center mb-2">Failed Logins</h3>
        <p className="text-center text-sm text-gray-600 mb-4">Critical security events</p>
        <div className="text-4xl font-bold text-center text-red-600">{failedLogins || 0}</div>
      </div>
    </div>
  );
  if (authError == "Unauthorized") {
    return <UnauthorizedPage />;
  } else{
  return (
    <div className="p-6 space-y-6">
      <SelectVariants 
        setaccountNames={(value) => handleFilterChange('accountName', value)}
        setRegion={(value) => handleFilterChange('region', value)}
        setDuration={(value) => handleFilterChange('duration', value)}
        region={filterState.region}
        accountName={filterState.accountName}
        duration={filterState.duration}
      />
      
      <div className="relative">
        {renderMetricsCards()}
        {isLoading && (
          <div className="absolute inset-0">
            <LoadingCards />
          </div>
        )}
      </div>

      <UserAuthDashboard 
        region={filterState.region}
        accountName={filterState.accountName}
        duration={filterState.duration}
        loginData={loginData}
        isLoading={isLoading}
        lastUpdatedTime={lastUpdatedTime}
      />
    </div>
  );
}
};

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let startTime;
    const startValue = 0;
    const endValue = parseFloat(value);
    const animation = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;

      if (progress < 1) {
        const currentCount = startValue + (endValue - startValue) * progress;
        setCount(currentCount);
        requestAnimationFrame(animation);
      } else {
        setCount(endValue);
      }
    };
    requestAnimationFrame(animation);
  }, [value, duration]);
  return <span>{count.toFixed(2)}</span>;
};


export default SecurityPage;